"use client";
import { ToggleTheme } from "@/components/toggle-theme";
import { Button } from "@/components/ui/button";
import {
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Sheet,
} from "@/components/ui/sheet";
import { Separator } from "@radix-ui/react-dropdown-menu";
import { Menu } from "lucide-react";
import { motion, Variants } from "framer-motion";
import React from "react";
import { Link } from "react-router-dom";

// Add new animations
const navVariants:Variants = {
  hidden: { y: -100 },
  visible: {
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 20,
    },
  },
};

const linkVariants = {
  hover: { scale: 1.05, color: "hsl(var(--primary))" },
};

interface RouteProps {
  to: string;
  label: string;
  className?: string;
}

const routeList: RouteProps[] = [
  {
    to: "/#home",
    label: "Home",
  },
  {
    to: "/#product",
    label: "Product",
  },
  {
    to: "/#testimonials",
    label: "Testimonials",
  },
  // pricing

  // {
  //   to: "/#pricing",
  //   label: "Pricing",
  // },
  {
    to: "/#contact",
    label: "Contact Us",
  },
  // {
  //   to: "/#faq",
  //   label: "FAQ",
  // },

  // {
  //   to: "/#about-us",
  //   label: "About Us"
  // },
  {
    to: "/sign-in",
    label: "Sign In",
    className: "",
  },
];

export const LandingNavbar = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [activeSection, setActiveSection] = React.useState("");

  // Add scroll handler
  React.useEffect(() => {
    const handleScroll = () => {
      const sections = routeList.map((route) => route.to.replace("#", ""));
      const currentSection = sections.find((section) => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (currentSection) setActiveSection(currentSection);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <motion.header
      variants={navVariants}
      initial="hidden"
      animate="visible"
      className="sticky top-0 z-50 w-full backdrop-blur-lg bg-background/80 border-b border-border/40 shadow-sm"
    >
      <div className="container flex h-16 items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex items-center space-x-2">
          <motion.img
            src="/logo/logo-secondary.png"
            className="h-8 rounded-md"
            whileHover={{ rotate: 360 }}
            transition={{ duration: 0.5 }}
          />
        </Link>

        {/* Mobile Navigation */}
        <div className="flex items-center lg:hidden">
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Menu
                onClick={() => setIsOpen(!isOpen)}
                className="cursor-pointer lg:hidden"
              />
            </SheetTrigger>

            <SheetContent
              side="left"
              className="flex flex-col justify-between rounded-tr-2xl rounded-br-2xl bg-card border-secondary"
            >
              <div>
                <SheetHeader className="mb-4 ml-4">
                  <SheetTitle className="flex items-center">
                    <Link to="/" className="flex items-center">
                      <img
                        src="/logo/logo-secondary.png"
                        alt="Mikrosell Car Wash POS"
                        className="h-8 mr-2 rounded-md"
                      />
                    </Link>
                  </SheetTitle>
                </SheetHeader>

                <div className="flex flex-col gap-2">
                  {routeList.map(({ to, label }) => (
                    <Button
                      key={to}
                      onClick={() => setIsOpen(false)}
                      asChild
                      variant="ghost"
                      className="justify-start text-base"
                    >
                      <Link
                        to={to}
                        onClick={() =>
                          document
                            .querySelector(to)
                            ?.scrollIntoView({ behavior: "smooth" })
                        }
                      >
                        {label}
                      </Link>
                    </Button>
                  ))}
                </div>
              </div>

              <SheetFooter className="flex-col sm:flex-col justify-start items-start">
                <Separator className="mb-2" />
                <ToggleTheme />
              </SheetFooter>
            </SheetContent>
          </Sheet>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-6">
          {routeList.map(({ to, label }) => (
            <motion.div
              key={to}
              variants={linkVariants}
              whileHover="hover"
              className="relative"
            >
              <Link
                to={to}
                onClick={() =>
                  document
                    .querySelector(to)
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  activeSection === to.replace("#", "")
                    ? "text-primary"
                    : "text-muted-foreground"
                }`}
              >
                {label}
                {activeSection === to.replace("#", "") && (
                  <motion.div
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary"
                    layoutId="activeSection"
                  />
                )}
              </Link>
            </motion.div>
          ))}

          <div className="flex items-center space-x-4">
            <ToggleTheme />
            <Link to="/sign-up">
              <Button className="bg-primary hover:bg-primary/90">
                Get Started
              </Button>
            </Link>
          </div>
        </nav>
      </div>
    </motion.header>
  );
};
